/**
 * Auth-group taxonomy shared by the Access Control section and the
 * admin-mode user-header role chips: the basic-role / view-only-addon
 * id constants, their display-label lookups, and the staff-only group
 * bucketing used by the staff auth-group editor.
 *
 * Contains:
 * - BASIC_AUTH_GROUPS, BASIC_AUTH_GROUP_IDS, VIEW_ONLY_ADDON_GROUPS
 * - basicGroupLabel, addonGroupLabel
 * - STAFF_GROUP_BUCKETS
 */

// The IDs are stable in production data and match what the legacy
// NonStaffAuthGroupSelectionComponent uses.  Basic groups are mutually
// exclusive; addon groups stack on top.
export const BASIC_AUTH_GROUPS = {
  RESELLER: 1061,
  ADMIN: 7,
  MANAGER: 8,
  USER: 9,
  VIEW_ONLY: 1054,
  THIRD_PARTY_ADMIN: 1063,
};
export const BASIC_AUTH_GROUP_IDS = Object.values(BASIC_AUTH_GROUPS);
// View-only addons — Schedule (10), Relay (1059), Intrusion (1060)
// match the legacy NonStaffAuthGroupSelectionComponent's editable
// surface, which only renders these toggles when the basic role is
// View-only.  The other addon ids in the legacy ADDON_GROUPS list
// (api-manager 1056, api-user 1057) aren't editable in that widget;
// we treat them as passthrough so a round-trip PATCH preserves
// whatever the user currently holds.
export const VIEW_ONLY_ADDON_GROUPS = [10, 1059, 1060];

// Pretty labels keyed off ID — kept here rather than fetching the
// names off /api/groups/ because the IDs are platform constants and
// the labels are user-facing copy that wants i18n keys anyway.
export const basicGroupLabel = (id, t) => {
  switch (id) {
    case BASIC_AUTH_GROUPS.RESELLER:
      return t('userSettings.reseller', 'Reseller');
    case BASIC_AUTH_GROUPS.ADMIN:
      return t('userSettings.administrator', 'Administrator');
    case BASIC_AUTH_GROUPS.MANAGER:
      return t('userSettings.siteGroupManager', 'Site Group Manager');
    case BASIC_AUTH_GROUPS.USER:
      return t('userSettings.user', 'User');
    case BASIC_AUTH_GROUPS.VIEW_ONLY:
      return t('userSettings.viewOnlyUser', 'View-only User');
    case BASIC_AUTH_GROUPS.THIRD_PARTY_ADMIN:
      return t('userSettings.thirdPartyAdmin', 'Third-party Admin');
    default:
      return null;
  }
};
export const addonGroupLabel = (id, t) => {
  switch (id) {
    case 10:
      return t('userSettings.scheduleControl', 'Schedule Control');
    case 1056:
      return t('userSettings.apiManager', 'API Manager');
    case 1057:
      return t('userSettings.apiUser', 'API User');
    case 1059:
      return t('userSettings.relayCommands', 'Relay Commands');
    case 1060:
      return t('userSettings.intrusionCommands', 'Intrusion Commands');
    default:
      return `#${id}`;
  }
};

// Categories for the staff-edit UI. Ordered as they appear in the
// rendered list. Group ids match the IDs in
// ``utils/userPermissionUtils.roleDisplayNameSwitch``. Anything an
// admin can assign that isn't in a named bucket falls into "Other"
// rendered last.
export const STAFF_GROUP_BUCKETS = [
  {
    labelKey: 'userSettings.staffGroups.staff',
    labelDefault: 'Staff',
    ids: new Set([
      1, // Engineering
      2, // Manufacturing
      3, // Shipping
      4, // Accounting
      5, // Sales
      6, // Tech Support
      11, // Technicians
    ]),
  },
  {
    labelKey: 'userSettings.staffGroups.admin',
    labelDefault: 'Admin',
    ids: new Set([
      7, // Administrator
      1066, // Inventory Admin
      1071, // Sales Admin
    ]),
  },
  {
    labelKey: 'userSettings.staffGroups.salesOps',
    labelDefault: 'Sales Ops',
    ids: new Set([
      1072, // Sales Person
    ]),
  },
  {
    labelKey: 'userSettings.staffGroups.integrations',
    labelDefault: 'Integrations & API',
    ids: new Set([
      1056, // API Manager
      1057, // API User
      1067, // QuickBooks User
    ]),
  },
];
